import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Plus, X, Truck, FileText, CheckCircle, Warning, Package, Camera } from "@phosphor-icons/react";

const INR = (n) => "\u20B9" + Number(n || 0).toLocaleString("en-IN");

export default function DispatchBoard() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const [orders, setOrders] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [drawerOrderId, setDrawerOrderId] = useState("");
  const [settings, setSettings] = useState({ margin_threshold_percent: 10, min_quotes_vendor: 3, min_quotes_freight: 3 });
  const [showSettings, setShowSettings] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scanInputRef = useRef(null);

  const handleScan = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setScanning(true);
    const t = toast.loading("Reading PO... extracting details");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/dispatch/orders/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.dismiss(t);
      const missing = [];
      if (!data.client_po_number) missing.push("PO number");
      if (!data.client_gstin) missing.push("GSTIN");
      if (!Number(data.client_po_amount)) missing.push("PO amount");
      if (missing.length) toast.warning(`${data.order_number} created · fill in: ${missing.join(", ")}`);
      else toast.success(`${data.order_number} created from scan`);
      await load();
      setDrawerOrderId(data.id);
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/dispatch/orders");
      setOrders(data.orders || []);
      setStages(data.stages || []);
    } catch (err) {
      toast.error("Failed to load dispatch board");
    } finally {
      setLoading(false);
    }
  };
  const loadSettings = async () => {
    try {
      const { data } = await api.get("/dispatch/settings");
      setSettings(data);
    } catch (_) {}
  };
  useEffect(() => { load(); loadSettings(); }, []);

  const columns = useMemo(() => {
    const map = {};
    stages.forEach((s) => { map[s.n] = { stage: s, cards: [] }; });
    orders.forEach((o) => {
      const n = o.current_stage || 1;
      if (map[n]) map[n].cards.push(o);
    });
    return Object.values(map);
  }, [orders, stages]);

  return (
    <Layout>
      <div className="px-6 py-6 min-h-screen bg-[#F5F3EF]">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A0A0A]">Dispatch Pipeline</h1>
            <div className="mt-1 text-[12px] text-[#717680]">
              13-stage blocking state machine · <b>{orders.length}</b> active order{orders.length !== 1 ? "s" : ""} · margin threshold <b>{settings.margin_threshold_percent}%</b> · min quotes <b>{settings.min_quotes_vendor}v / {settings.min_quotes_freight}f</b>
            </div>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <button data-testid="dispatch-settings-btn" onClick={() => setShowSettings(true)}
                className="px-4 py-2 border border-black/15 text-sm hover:bg-black hover:text-white">Settings</button>
            )}
            <input
              ref={scanInputRef}
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              className="hidden"
              onChange={handleScan}
              data-testid="dispatch-scan-input"
            />
            <button
              data-testid="dispatch-scan-btn"
              onClick={() => scanInputRef.current?.click()}
              disabled={scanning}
              className="md:hidden px-3 py-2 border border-black/15 text-sm hover:bg-black hover:text-white flex items-center gap-2 disabled:opacity-50"
              title="Scan a Client PO photo to auto-create the order"
            >
              <Camera size={16} weight="bold" /> {scanning ? "Scanning..." : "Scan PO"}
            </button>
            <button data-testid="dispatch-new-order-btn" onClick={() => setShowCreate(true)}
              className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] flex items-center gap-2">
              <Plus size={16} weight="bold" /> New Dispatch Order
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-[#717680]">Loading pipeline...</div>
        ) : orders.length === 0 ? (
          <div className="bg-white border border-black/10 p-12 text-center">
            <Truck size={56} weight="duotone" className="mx-auto text-[#D90429]" />
            <div className="mt-3 font-display text-xl">No dispatch orders yet</div>
            <div className="mt-1 text-sm text-[#717680]">Create a dispatch order linked to an existing sales invoice or client PO to start the 13-stage workflow.</div>
            <button data-testid="dispatch-empty-create-btn" onClick={() => setShowCreate(true)}
              className="mt-5 px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">
              Create first order
            </button>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-6" data-testid="dispatch-board">
            {columns.map(({ stage, cards }) => (
              <div key={stage.n} className="min-w-[270px] max-w-[270px] bg-white border border-black/10 flex flex-col" data-testid={`dispatch-col-${stage.n}`}>
                <div className="px-3 py-2 border-b border-black/10 bg-[#0A0A0A] text-white">
                  <div className="text-[10px] font-mono-shelv uppercase tracking-widest opacity-70">Stage {stage.n}</div>
                  <div className="text-sm font-bold leading-tight">{stage.label}</div>
                  <div className="text-[10px] opacity-60 mt-0.5">{cards.length} order{cards.length !== 1 ? "s" : ""}</div>
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[70vh]">
                  {cards.map((o) => (
                    <button key={o.id} onClick={() => setDrawerOrderId(o.id)}
                      data-testid={`dispatch-card-${o.order_number}`}
                      className="w-full text-left border border-black/15 bg-[#FAFAF7] p-2.5 hover:bg-white hover:border-[#D90429] transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="font-mono-shelv text-[11px] font-bold text-[#D90429]">{o.order_number}</div>
                        <div className="flex items-center gap-1">
                          {o.status === "on_hold" && <span className="text-[9px] font-mono-shelv tracking-wider text-[#CA8A04] bg-[#CA8A04]/10 px-1" title={o.hold_reason}>HOLD</span>}
                          {o.status === "closed" && <CheckCircle size={14} weight="fill" className="text-[#0D9488]" />}
                        </div>
                      </div>
                      <div className="text-xs font-semibold text-[#0A0A0A] mt-1 truncate">{o.client_name}</div>
                      <div className="text-[10px] text-[#717680] mt-1 flex items-center gap-2">
                        <span>{INR(o.client_po_amount)}</span>
                        {o.margin_locked && <span className="font-mono-shelv text-[#0D9488]">{o.margin_percent}%</span>}
                      </div>
                      <div className="text-[10px] text-[#717680] mt-0.5 flex items-center justify-between">
                        <span>V:{o.vendor_quote_count || 0} · F:{o.freight_quote_count || 0}</span>
                        <span className="font-mono-shelv" data-testid={`card-days-${o.order_number}`}>{o.days_in_stage || 0}d in stage</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate && <CreateOrderModal onClose={() => setShowCreate(false)} onCreated={(o) => { setShowCreate(false); load(); setDrawerOrderId(o.id); }} />}
      {showSettings && <SettingsModal settings={settings} onClose={() => setShowSettings(false)} onSaved={(s) => { setSettings(s); setShowSettings(false); toast.success("Settings saved"); }} />}
      {drawerOrderId && <OrderDrawer orderId={drawerOrderId} onClose={() => setDrawerOrderId("")} onChange={load} isAdmin={isAdmin} />}
    </Layout>
  );
}

/* -------------------------- Create Modal ------------------------------- */
function CreateOrderModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ client_name: "", client_gstin: "", client_po_number: "", client_po_date: "", client_po_amount: 0, client_state: "", billing_doc_id: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.client_name.trim()) { toast.error("Client name required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/dispatch/orders", { ...form, client_po_amount: Number(form.client_po_amount) || 0 });
      toast.success(`${data.order_number} created`);
      onCreated(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="dispatch-create-modal">
      <div className="bg-white border border-black/15 max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold">New Dispatch Order</h3>
          <button onClick={onClose} className="text-[#717680] hover:text-black"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Client name *"><input data-testid="create-client-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
          <Field label="Consignee GSTIN *"><input data-testid="create-gstin" value={form.client_gstin} onChange={(e) => setForm({ ...form, client_gstin: e.target.value.toUpperCase() })} placeholder="22AAAAA0000A1Z5" className="w-full border border-black/15 px-3 py-2 text-sm font-mono" /></Field>
          <Field label="Linked sales doc (billing_doc_id, optional)"><input data-testid="create-billing-doc-id" value={form.billing_doc_id} onChange={(e) => setForm({ ...form, billing_doc_id: e.target.value })} placeholder="bill_..." className="w-full border border-black/15 px-3 py-2 text-xs font-mono" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Client PO number *"><input data-testid="create-po-number" value={form.client_po_number} onChange={(e) => setForm({ ...form, client_po_number: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
            <Field label="Client PO date"><input type="date" data-testid="create-po-date" value={form.client_po_date} onChange={(e) => setForm({ ...form, client_po_date: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Client PO amount (sale price) *"><input type="number" data-testid="create-po-amount" value={form.client_po_amount} onChange={(e) => setForm({ ...form, client_po_amount: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
            <Field label="Consignee state"><input data-testid="create-state" value={form.client_state} onChange={(e) => setForm({ ...form, client_state: e.target.value })} placeholder="e.g. Nagaland" className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
          </div>
          <div className="text-[11px] text-[#717680]">Stage 1 won't release until GSTIN, PO number and a non-zero sale amount are all filled.</div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="create-submit-btn" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold disabled:opacity-40">{saving ? "Creating..." : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------- Settings Modal ----------------------------- */
function SettingsModal({ settings, onClose, onSaved }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/dispatch/settings", {
        margin_threshold_percent: Number(form.margin_threshold_percent),
        min_quotes_vendor: Number(form.min_quotes_vendor),
        min_quotes_freight: Number(form.min_quotes_freight),
      });
      onSaved(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-black/15 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold">Dispatch Settings</h3>
          <button onClick={onClose} className="text-[#717680]"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Margin threshold (%)"><input type="number" step="0.5" data-testid="setting-margin" value={form.margin_threshold_percent} onChange={(e) => setForm({ ...form, margin_threshold_percent: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min vendor quotes"><input type="number" data-testid="setting-min-vendor" value={form.min_quotes_vendor} onChange={(e) => setForm({ ...form, min_quotes_vendor: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
            <Field label="Min freight quotes"><input type="number" data-testid="setting-min-freight" value={form.min_quotes_freight} onChange={(e) => setForm({ ...form, min_quotes_freight: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="setting-save-btn" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold disabled:opacity-40">{saving ? "Saving..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------- Order Drawer ------------------------------- */
function OrderDrawer({ orderId, onClose, onChange, isAdmin }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [advancing, setAdvancing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/dispatch/orders/${orderId}`);
      setData(data);
    } catch (_) { toast.error("Failed to load order"); }
  };
  useEffect(() => { load(); }, [orderId]);

  if (!data) return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full p-6">Loading...</div>
    </div>
  );

  const { order, quotes, current_stage, next_stage_block_reason, stages } = data;
  const stageMeta = stages.find((s) => s.n === current_stage) || {};
  const nextStage = stages.find((s) => s.n === current_stage + 1);

  const advance = async (overrideReason = "", extra = {}) => {
    setAdvancing(true);
    try {
      await api.post(`/dispatch/orders/${orderId}/advance`, { note: "", override_reason: overrideReason, ...extra });
      toast.success(`Advanced to Stage ${current_stage + 1}`);
      await load(); onChange();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? (detail.block_reason || detail.error || "Blocked") : (detail || "Advance failed");
      toast.error(msg);
    } finally { setAdvancing(false); }
  };

  const putOnHold = async () => {
    const reason = window.prompt("Reason for putting this order on hold (mandatory):");
    if (!reason || !reason.trim()) { toast.error("Reason required"); return; }
    try {
      await api.post(`/dispatch/orders/${orderId}/hold`, { reason });
      toast.success("Order put on hold");
      await load(); onChange();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "object" ? (detail.block_reason || detail.error) : (detail || "Hold failed"));
    }
  };
  const resumeOrder = async () => {
    try {
      await api.post(`/dispatch/orders/${orderId}/resume`);
      toast.success("Order resumed");
      await load(); onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Resume failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-end" onClick={onClose} data-testid="dispatch-drawer">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-black/10 px-5 py-3 flex items-center justify-between z-10">
          <div className="flex-1 min-w-0">
            <div className="font-mono-shelv text-[11px] text-[#D90429] font-bold">{order.order_number}</div>
            <div className="font-display text-lg font-bold truncate">{order.client_name}</div>
            <div className="text-[11px] text-[#717680]">
              Stage {current_stage}/13 · {stageMeta.label} · <b className={order.status === "on_hold" ? "text-[#CA8A04]" : order.status === "closed" ? "text-[#0D9488]" : "text-[#0A0A0A]"}>{order.status}</b>
              {order.status === "on_hold" && order.hold_reason && <span> · {order.hold_reason}</span>}
              {order.days_in_stage !== undefined && <span> · {order.days_in_stage}d in stage</span>}
            </div>
          </div>
          <div className="flex items-center gap-1 ml-2">
            {order.status === "open" && order.current_stage < 13 && (
              <button onClick={putOnHold} data-testid="btn-hold" className="px-2 py-1 text-[10px] uppercase font-mono-shelv tracking-widest border border-[#CA8A04] text-[#CA8A04] hover:bg-[#CA8A04] hover:text-white">Hold</button>
            )}
            {order.status === "on_hold" && (
              <button onClick={resumeOrder} data-testid="btn-resume" className="px-2 py-1 text-[10px] uppercase font-mono-shelv tracking-widest border border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488] hover:text-white">Resume</button>
            )}
            <button onClick={onClose} className="text-[#717680] hover:text-black p-2"><X size={18} /></button>
          </div>
        </div>

        <div className="flex border-b border-black/10 px-5">
          {["overview", "quotes", "attachments", "history"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`px-3 py-2 text-xs uppercase font-mono-shelv tracking-widest ${tab === t ? "border-b-2 border-[#D90429] text-[#D90429]" : "text-[#717680]"}`}>{t}</button>
          ))}
        </div>

        <div className="p-5">
          {tab === "overview" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <KV label="Client PO #" value={order.client_po_number || "-"} />
                <KV label="Client PO date" value={order.client_po_date || "-"} />
                <KV label="Client PO amount" value={INR(order.client_po_amount)} />
                <KV label="Consignee GSTIN" value={order.client_gstin || "-"} mono />
                <KV label="Consignee state" value={order.client_state || "-"} />
                <KV label="Margin" value={order.margin_locked ? `${order.margin_percent}%` : "Not locked"} />
                <KV label="Billing doc" value={order.billing_doc_id || "-"} mono />
                <KV label="Vendor PO" value={order.vendor_po_purchase_id || "-"} mono />
                <KV label="Vendor payment" value={order.vendor_payment_id || "-"} mono />
                <KV label="Sales invoice (tax)" value={order.sales_invoice_doc_id || "-"} mono />
                <KV label="LR number" value={order.lr_number || "-"} />
                <KV label="Vehicle number" value={order.vehicle_number || "-"} />
                <KV label="E-way number" value={order.eway_number || "-"} />
                <KV label="E-way expiry" value={order.eway_expiry || "-"} />
                <KV label="Driver phone" value={order.driver_phone || "-"} />
              </div>

              {next_stage_block_reason && (
                <div className="border border-[#D90429]/30 bg-[#FFE5E9]/40 p-3 text-[12px]" data-testid="block-reason">
                  <div className="flex items-center gap-2 font-semibold text-[#D90429]"><Warning size={14} weight="fill" /> Cannot advance to Stage {current_stage + 1} ({nextStage?.label})</div>
                  <div className="mt-1 text-[#0A0A0A]">{next_stage_block_reason}</div>
                </div>
              )}

              <AdvancePanel order={order} nextStage={nextStage} blockReason={next_stage_block_reason} isAdmin={isAdmin} advance={advance} advancing={advancing} orderId={orderId} onChange={load} />
            </div>
          )}

          {tab === "quotes" && <QuotesTab orderId={orderId} quotes={quotes} order={order} onChange={load} />}
          {tab === "attachments" && <AttachmentsTab orderId={orderId} attachments={order.attachments || []} onChange={load} />}
          {tab === "history" && (
            <div className="space-y-2">
              {(order.stage_history || []).length === 0 && <div className="text-sm text-[#717680]">No stage transitions yet.</div>}
              {(order.stage_history || []).map((h, i) => (
                <div key={i} className="border border-black/10 bg-[#FAFAF7] p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-mono-shelv text-[11px] font-bold">Stage {h.stage_n} · {stages.find((s) => s.n === h.stage_n)?.label}</span>
                    <span className="text-[#717680]">{new Date(h.advanced_at).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="mt-1">By: <b>{h.advanced_by_name || h.advanced_by_user_id}</b></div>
                  {h.override && <div className="mt-1 text-[#D90429]"><b>Override:</b> {h.override_reason}</div>}
                  {h.note && <div className="mt-1 text-[#717680]">Note: {h.note}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AdvancePanel({ order, nextStage, blockReason, isAdmin, advance, advancing, orderId, onChange }) {
  const [overrideReason, setOverrideReason] = useState("");
  const [extra, setExtra] = useState({});
  const stage = order.current_stage;
  if (stage >= 13) return <div className="text-sm text-[#0D9488] font-semibold flex items-center gap-2"><CheckCircle size={16} weight="fill" /> Order closed.</div>;

  const canOverride = isAdmin && [2, 3, 4].includes(stage + 1) && !!blockReason;

  // Stage-specific extra input hints
  const stageInputs = () => {
    const target = stage + 1;
    if (target === 6) return <Field label="Vendor PO purchase_id"><input placeholder="pur_..." className="w-full border border-black/15 px-3 py-2 text-xs font-mono" onChange={(e) => setExtra({ ...extra, vendor_po_purchase_id: e.target.value })} data-testid="adv-vendor-po-id" /></Field>;
    if (target === 7) return <Field label="Vendor payment_id"><input placeholder="pay_..." className="w-full border border-black/15 px-3 py-2 text-xs font-mono" onChange={(e) => setExtra({ ...extra, vendor_payment_id: e.target.value })} data-testid="adv-vendor-pay-id" /></Field>;
    if (target === 9) return <Field label="Verified sales invoice doc_id (kind=tax_invoice)"><input placeholder="bill_..." className="w-full border border-black/15 px-3 py-2 text-xs font-mono" onChange={(e) => setExtra({ ...extra, sales_invoice_doc_id: e.target.value })} data-testid="adv-sales-inv-id" /></Field>;
    if (target === 10) return (
      <div className="space-y-2">
        <Field label="Driver phone"><input className="w-full border border-black/15 px-3 py-2 text-sm" onChange={(e) => setExtra({ ...extra, driver_phone: e.target.value })} data-testid="adv-driver-phone" /></Field>
        <Field label="Vehicle number"><input className="w-full border border-black/15 px-3 py-2 text-sm" onChange={(e) => setExtra({ ...extra, vehicle_number: e.target.value.toUpperCase() })} placeholder="MH 12 AB 1234" data-testid="adv-vehicle-no" /></Field>
        <Field label="LR number"><input className="w-full border border-black/15 px-3 py-2 text-sm" onChange={(e) => setExtra({ ...extra, lr_number: e.target.value })} data-testid="adv-lr" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="E-way number"><input className="w-full border border-black/15 px-3 py-2 text-sm" onChange={(e) => setExtra({ ...extra, eway_number: e.target.value })} data-testid="adv-eway" /></Field>
          <Field label="E-way expiry"><input type="date" className="w-full border border-black/15 px-3 py-2 text-sm" onChange={(e) => setExtra({ ...extra, eway_expiry: e.target.value })} data-testid="adv-eway-exp" /></Field>
        </div>
      </div>
    );
    return null;
  };

  const compute3Way = async () => {
    try {
      const { data } = await api.get(`/dispatch/orders/${orderId}/three-way`);
      if (data.passed) toast.success("3-way match passed ✓"); else toast.error("3-way match failed — see Overview");
      onChange();
    } catch (_) { toast.error("Compute failed"); }
  };

  return (
    <div className="border border-black/10 bg-[#FAFAF7] p-3 space-y-3">
      <div className="text-xs text-[#717680] uppercase font-mono-shelv tracking-widest">Advance to Stage {stage + 1}: <b className="text-[#0A0A0A]">{nextStage?.label}</b></div>
      {stage + 1 === 8 && <button data-testid="btn-compute-3way" onClick={compute3Way} className="px-3 py-2 border border-black/15 text-xs hover:bg-black hover:text-white">Compute 3-way match</button>}
      {stage + 1 === 5 && !order.margin_locked && <div className="text-[11px] text-[#D90429]">Go to Quotes tab → select winning vendor + freight to compute margin.</div>}
      {stageInputs()}
      {canOverride && (
        <Field label="Admin override reason (bypasses the current block)">
          <textarea data-testid="adv-override-reason" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} rows={2} className="w-full border border-[#D90429]/40 px-3 py-2 text-xs" placeholder="Why is <3 quotes acceptable this time?" />
        </Field>
      )}
      <button data-testid="btn-advance" onClick={() => advance(overrideReason, extra)} disabled={advancing}
        className="w-full px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-40">
        {advancing ? "Advancing..." : "Advance stage"}
      </button>
    </div>
  );
}

/* -------------------------- Quotes Tab --------------------------------- */
function QuotesTab({ orderId, quotes, order, onChange }) {
  const [kind, setKind] = useState("vendor");
  const [form, setForm] = useState({ party_name: "", amount: "", item_description: "", lane: "", vehicle_type: "", delivery_days: 0, eta_days: 0, notes: "" });
  const [saving, setSaving] = useState(false);

  const addQuote = async () => {
    if (!form.party_name.trim() || Number(form.amount) <= 0) { toast.error("Party name + amount required"); return; }
    if (kind === "freight") {
      if (!form.lane.trim() || !form.vehicle_type.trim() || Number(form.eta_days) <= 0) {
        toast.error("Freight needs lane, vehicle type and ETA days (>0)"); return;
      }
    }
    setSaving(true);
    try {
      await api.post(`/dispatch/orders/${orderId}/quotes`, { quote_kind: kind, ...form, amount: Number(form.amount) });
      toast.success("Quote added");
      setForm({ party_name: "", amount: "", item_description: "", lane: "", vehicle_type: "", delivery_days: 0, eta_days: 0, notes: "" });
      onChange();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "object" ? (detail.block_reason || detail.error) : (detail || "Add failed"));
    }
    finally { setSaving(false); }
  };
  const remove = async (qid) => {
    if (!window.confirm("Delete this quote?")) return;
    await api.delete(`/dispatch/orders/${orderId}/quotes/${qid}`);
    onChange();
  };
  const select = async (kind, qid) => {
    try {
      await api.post(`/dispatch/orders/${orderId}/select-quote`, kind === "vendor" ? { vendor_quote_id: qid } : { freight_quote_id: qid });
      toast.success("Selected · margin recomputed");
      onChange();
    } catch (err) { toast.error("Select failed"); }
  };

  const vendorQs = quotes.filter((q) => q.quote_kind === "vendor");
  const freightQs = quotes.filter((q) => q.quote_kind === "freight");

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-mono-shelv uppercase tracking-widest text-[#717680]">Vendor Quotes ({vendorQs.length})</div>
          {order.selected_vendor_quote_id && <div className="text-[10px] text-[#0D9488] font-mono-shelv">LOCKED</div>}
        </div>
        <QuoteList list={vendorQs} selectedId={order.selected_vendor_quote_id} onSelect={(id) => select("vendor", id)} onDelete={remove} kind="vendor" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-mono-shelv uppercase tracking-widest text-[#717680]">Freight Quotes ({freightQs.length})</div>
          {order.selected_freight_quote_id && <div className="text-[10px] text-[#0D9488] font-mono-shelv">LOCKED</div>}
        </div>
        <QuoteList list={freightQs} selectedId={order.selected_freight_quote_id} onSelect={(id) => select("freight", id)} onDelete={remove} kind="freight" />
      </div>

      <div className="border-t border-black/10 pt-4">
        <div className="text-xs font-mono-shelv uppercase tracking-widest text-[#717680] mb-2">Add Quote</div>
        <div className="flex gap-2 mb-2">
          <button data-testid="quote-kind-vendor" onClick={() => setKind("vendor")} className={`px-3 py-1 text-xs border ${kind === "vendor" ? "bg-black text-white border-black" : "border-black/15"}`}>Vendor</button>
          <button data-testid="quote-kind-freight" onClick={() => setKind("freight")} className={`px-3 py-1 text-xs border ${kind === "freight" ? "bg-black text-white border-black" : "border-black/15"}`}>Freight</button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Party name *"><input data-testid="quote-party-name" value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
          <Field label="Amount (INR) *"><input type="number" data-testid="quote-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
          {kind === "vendor" ? (
            <>
              <Field label="Item description"><input value={form.item_description} onChange={(e) => setForm({ ...form, item_description: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
              <Field label="Delivery (days)"><input type="number" value={form.delivery_days} onChange={(e) => setForm({ ...form, delivery_days: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
            </>
          ) : (
            <>
              <Field label="Lane *"><input value={form.lane} onChange={(e) => setForm({ ...form, lane: e.target.value })} placeholder="Delhi → Guwahati" className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
              <Field label="Vehicle type *"><input value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} placeholder="20ft container · 32ft open" className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
              <Field label="ETA (days) *"><input type="number" value={form.eta_days} onChange={(e) => setForm({ ...form, eta_days: e.target.value })} className="w-full border border-black/15 px-3 py-2 text-sm" /></Field>
            </>
          )}
        </div>
        <button data-testid="quote-add-btn" onClick={addQuote} disabled={saving} className="mt-3 px-4 py-2 bg-[#D90429] text-white text-sm font-semibold disabled:opacity-40">{saving ? "Adding..." : "Add Quote"}</button>
      </div>
    </div>
  );
}

function QuoteList({ list, selectedId, onSelect, onDelete, kind }) {
  if (list.length === 0) return <div className="text-xs text-[#717680] italic">No {kind} quotes yet.</div>;
  return (
    <div className="space-y-1.5">
      {list.map((q) => (
        <div key={q.id} data-testid={`quote-row-${q.id}`} className={`border p-2 flex items-center justify-between gap-2 ${q.id === selectedId ? "border-[#0D9488] bg-[#0D9488]/5" : "border-black/15 bg-white"}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold truncate">{q.party_name}</span>
              {q.is_l1 && <span className="text-[9px] font-mono-shelv font-bold tracking-wider text-white bg-[#0D9488] px-1" title="Lowest amount">L1</span>}
              {q.id === selectedId && <CheckCircle size={13} weight="fill" className="text-[#0D9488]" />}
              {q.id === selectedId && !q.is_l1 && <span className="text-[9px] font-mono-shelv font-bold tracking-wider text-white bg-[#D90429] px-1" title="Not L1 — needs reason + director approval">!L1</span>}
            </div>
            <div className="text-[11px] text-[#717680]">
              {INR(q.amount)}{kind === "vendor" && q.delivery_days ? ` · ${q.delivery_days}d` : ""}{kind === "freight" && q.lane ? ` · ${q.lane}` : ""}{kind === "freight" && q.vehicle_type ? ` · ${q.vehicle_type}` : ""}{kind === "freight" && q.eta_days ? ` · ETA ${q.eta_days}d` : ""}
            </div>
          </div>
          {q.id !== selectedId && <button onClick={() => onSelect(q.id)} data-testid={`quote-select-${q.id}`} className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#D90429] hover:underline">Select</button>}
          <button onClick={() => onDelete(q.id)} data-testid={`quote-del-${q.id}`} className="text-[#717680] hover:text-[#D90429]"><X size={14} /></button>
        </div>
      ))}
    </div>
  );
}

/* -------------------------- Attachments Tab ---------------------------- */
function AttachmentsTab({ orderId, attachments, onChange }) {
  const [kind, setKind] = useState("client_po");
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("kind", kind);
    setUploading(true);
    try {
      await api.post(`/dispatch/orders/${orderId}/attach`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attached");
      onChange();
      e.target.value = "";
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const KINDS = ["client_po", "vendor_po", "sales_invoice", "eway", "lr", "pod", "open_box", "other"];
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs font-mono-shelv uppercase tracking-widest text-[#717680] mb-2">Upload artifact</div>
        <div className="flex flex-wrap gap-1 mb-2">
          {KINDS.map((k) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`att-kind-${k}`}
              className={`px-2 py-1 text-[10px] font-mono-shelv uppercase border ${kind === k ? "bg-black text-white border-black" : "border-black/15"}`}>{k}</button>
          ))}
        </div>
        <input type="file" data-testid="att-file-input" onChange={upload} disabled={uploading} className="text-xs" />
      </div>
      <div>
        <div className="text-xs font-mono-shelv uppercase tracking-widest text-[#717680] mb-2">Attached ({attachments.length})</div>
        {attachments.length === 0 && <div className="text-sm text-[#717680] italic">No attachments yet.</div>}
        <div className="space-y-1.5">
          {attachments.map((a) => (
            <div key={a.id} className="border border-black/15 bg-[#FAFAF7] p-2 flex items-center justify-between" data-testid={`att-row-${a.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={14} className="text-[#D90429] shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs font-semibold truncate">{a.filename}</div>
                  <div className="text-[10px] text-[#717680] font-mono-shelv uppercase">{a.kind} · {(a.size / 1024).toFixed(1)} KB</div>
                </div>
              </div>
              <a href={`/api/dispatch/orders/${orderId}/attachments/${a.id}`} target="_blank" rel="noreferrer" className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#D90429] hover:underline">Open</a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------- Small atoms -------------------------------- */
function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#717680]">{label}</label>
      {children}
    </div>
  );
}
function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#717680]">{label}</div>
      <div className={`text-sm text-[#0A0A0A] ${mono ? "font-mono text-[11px]" : ""} break-all`}>{value}</div>
    </div>
  );
}
